// src/components/discover/public-character-generator.tsx
"use client";

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { useAuth } from '@/context/auth-context';
import { useI18n } from '@/context/i18n-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Sparkles, Loader2, ArrowRight, RefreshCw, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Character } from '@/lib/types';
import { generatePublicCharacter } from '@/ai/flows/generate-public-character-flow';
import { Skeleton } from '@/components/ui/skeleton';


type GenerationStage = 'idle' | 'thinking' | 'generatingCharacter' | 'generatingImage' | 'completed' | 'error';

export default function PublicCharacterGenerator() {
  const { user, openAuthModal } = useAuth();
  const { t } = useI18n();
  const router = useRouter();
  
  const [prompt, setPrompt] = useState('');
  const [stage, setStage] = useState<GenerationStage>('idle');
  const [generatedCharacter, setGeneratedCharacter] = useState<Character | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stageMessages: Record<Exclude<GenerationStage, 'idle' | 'completed' | 'error'>, string> = {
    thinking: t('thinking') || "Thinking...",
    generatingCharacter: t('generatingCharacter') || "Generating Character...",
    generatingImage: t('generatingImage') || "Generating Image...",
  };

  const handleCreate = async () => {
    if (!prompt.trim()) return;
    
    setError(null);
    setGeneratedCharacter(null);
    
    setStage('thinking');
    
    try {
        const result = await generatePublicCharacter({
            prompt,
            userId: user?.uid,
            creatorHandle: user?.displayName || undefined
        });

        if (result.character) {
            setGeneratedCharacter(result.character);
            setStage('completed');
        } else {
            throw new Error("Character generation returned no data.");
        }
    } catch (e: any) {
        console.error("Public character generation failed:", e);
        setError(e.message || "An unknown error occurred during character generation.");
        setStage('error');
    }
  };

  const handleReset = () => {
    setPrompt('');
    setStage('idle');
    setGeneratedCharacter(null);
    setError(null);
  };

  const handleStartChat = () => {
    if (!generatedCharacter) return;
    const chatUrl = `/chat/${generatedCharacter.username || generatedCharacter.id}`;
    
    if (user) {
        router.push(chatUrl);
    } else {
        openAuthModal('login', chatUrl);
    }
  };
  
  const renderErrorState = (errorMessage: string | null) => (
    <div className="flex flex-col items-start justify-center w-full text-left">
      <Alert variant="destructive" className="bg-destructive/20 border-destructive text-destructive-foreground w-full">
        <AlertTriangle className="h-4 w-4"/>
        <AlertTitle>{t('generationError') || "Generation Error"}</AlertTitle>
        <AlertDescription className="text-xs">{errorMessage || "An unknown error occurred."}</AlertDescription>
      </Alert>
      <div className="mt-4 flex w-full">
          <Button onClick={handleReset} variant="outline" size="lg" className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white rounded-full h-12 px-6">
              <RefreshCw className="mr-2 h-4 w-4"/>
              {t('tryAgain')}
          </Button>
      </div>
    </div>
  );

  const renderIdleContent = () => (
    <>
      <div className="flex-grow flex flex-col justify-center gap-3 w-full max-w-2xl text-center md:text-left">
        <h2 className="text-3xl md:text-4xl font-bold text-white">Create & Chat</h2>
        <p className="text-white/80">Describe a character to create and chat with instantly.</p>
        <div className="flex items-center w-full h-16 md:h-14 bg-white/5 border border-white/20 rounded-full shadow-lg mt-2 p-1.5 gap-1.5">
          <div className="relative flex-grow h-full [mask-image:linear-gradient(to_right,hsl(0_0%_0%)_95%,transparent_100%)]">
            <Input
              type="text"
              placeholder={t('generateCharacterPlaceholder') || "Describe a character to create & chat with..."}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="h-full w-full text-base bg-transparent border-none text-white placeholder:text-white/60 focus:ring-0 pl-5"
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
          </div>
          <Button
            onClick={handleCreate}
            disabled={!prompt.trim() || stage !== 'idle'}
            className="h-full px-5 bg-primary hover:bg-primary/90 rounded-full flex-shrink-0"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">{t('createAndChat')}</span>
            <span className="sm:hidden">{t('create')}</span>
          </Button>
        </div>
      </div>
       <div className="h-96 w-full max-w-[500px] hidden md:block">
            <Image
                src="/home1.png"
                alt="Character illustration"
                width={500}
                height={500}
                className="object-contain w-full h-full"
                data-ai-hint="character illustration"
                priority
            />
        </div>
    </>
  );
  
  const renderLoadingContent = () => (
     <>
        <div className="relative w-full aspect-[3/4] max-w-[192px] rounded-2xl flex-shrink-0">
          <Skeleton className="absolute inset-0 bg-white/10" />
        </div>
        <div className="flex-grow flex flex-col justify-center text-left max-w-md w-full">
             <p className="text-sm text-primary font-semibold">{stageMessages[stage as keyof typeof stageMessages]}</p>
             <h3 className="text-3xl font-bold text-white mt-1 break-words">"{prompt}"</h3>
             <div className="flex flex-wrap gap-2 mt-3 justify-start">
                <Skeleton className="h-5 w-16 rounded-full bg-white/10" />
                <Skeleton className="h-5 w-20 rounded-full bg-white/10" />
                <Skeleton className="h-5 w-14 rounded-full bg-white/10" />
             </div>
             <div className="flex flex-col sm:flex-row gap-3 mt-5 justify-start">
                <Skeleton className="h-12 px-5 rounded-full bg-white/10 w-40" />
                <Skeleton className="h-12 px-5 rounded-full bg-white/10 w-40" />
             </div>
        </div>
    </>
  );

  const renderCompletedContent = () => {
     if (!generatedCharacter) return renderErrorState("Character data is missing.");
     return (
        <>
            <div className="relative aspect-[3/4] w-48 rounded-2xl overflow-hidden shadow-2xl flex-shrink-0 border-2 border-primary/50 group">
               <Image
                  src={generatedCharacter.imageUrl}
                  alt={generatedCharacter.name}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-300"
                  data-ai-hint={generatedCharacter.imageHint || 'character portrait'}
               />
               <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
            </div>
            <div className="flex-grow flex flex-col justify-center text-left max-w-md w-full">
              <p className="text-sm text-primary font-semibold">{t('characterCreated')}</p>
              <h3 className="text-3xl font-bold text-white mt-1">{generatedCharacter.name}</h3>
              <p className="text-sm text-white/80 mt-2 line-clamp-3">{generatedCharacter.description}</p>
              <div className="flex flex-wrap gap-2 mt-3 justify-start">
                {generatedCharacter.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="bg-white/10 text-white/90 border-transparent">{tag}</Badge>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-5 justify-start">
                  <Button onClick={handleStartChat} size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-12 px-6">
                    <ArrowRight className="mr-2 h-4 w-4"/>
                    {t('startChat')}
                  </Button>
                  <Button onClick={handleReset} size="lg" variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white rounded-full h-12 px-6">
                    <RefreshCw className="mr-2 h-4 w-4"/>
                    {t('createAnother')}
                  </Button>
              </div>
               {!user && (
                  <p className="text-xs text-white/70 mt-4 text-left">
                    <button onClick={handleStartChat} className="font-semibold underline hover:text-white">
                        {t('loginToSave')}
                    </button> {t('toSaveCharacter')}
                  </p>
                )}
            </div>
        </>
     );
  }

  return (
    <div className="w-full max-w-6xl">
      <div className="flex items-center justify-center gap-x-8 gap-y-6 min-h-[350px] animate-in fade-in-50 duration-500">
        {stage === 'idle' && renderIdleContent()}
        {(stage === 'thinking' || stage === 'generatingCharacter' || stage === 'generatingImage') && renderLoadingContent()}
        {stage === 'completed' && renderCompletedContent()}
        {stage === 'error' && (
          <div className="w-full max-w-xl">
            {renderErrorState(error)}
          </div>
        )}
      </div>
    </div>
  );
}
